<template>
  <div class="roompad-page invisible-scrollbar">
    <section class="roompad-toolbar" :aria-label="$t('management.roomPadActions')">
      <div class="roompad-search">
        <input
          v-model.trim="search"
          type="search"
          :placeholder="$t('management.search')"
          :aria-label="$t('management.searchRoomPad')"
          @keydown.enter="SearchDataRoomPad"
        />
        <button
          type="button"
          :aria-label="$t('management.search')"
          @click="SearchDataRoomPad"
        >
          <img
            src="@/assets/figma/search-normal.svg"
            alt=""
            aria-hidden="true"
          />
        </button>
      </div>
      <button
        class="create-roompad"
        type="button"
        @click="OpenAddRoomPadDialog"
      >
        {{ $t("management.create") }} +
      </button>
    </section>

    <section class="roompad-panel">
      <div class="roompad-table-wrap">
        <v-data-table
          class="roompad-table"
          :headers="headers"
          :items="ListData"
          :items-per-page="itemsPerPage"
          item-key="id"
          hide-default-footer
          disable-sort
        >
          <template v-slot:item.no="{ index }">{{
            no_run + index + 1
          }}</template>
          <template v-slot:item.active="{ item }"
            ><span :class="['roompad-status', { inactive: !item.active }]">{{
              item.active ? $t("management.active") : $t("management.inactive")
            }}</span></template
          >
          <template v-slot:item.action="{ item }"
            ><div class="roompad-actions">
              <button
                type="button"
                :aria-label="$t('management.editRoomPad')"
                @click="OpenDetailRoomPadDialog(item.id)"
              >
                <img
                  class="roompad-action-icon roompad-action-icon--edit"
                  src="@/assets/icons/brush.svg"
                  alt=""
                  aria-hidden="true"
                /></button
              ><button
                class="delete"
                type="button"
                :disabled="!item.active || LoadingDialog"
                :aria-label="$t('management.deleteRoomPad')"
                @click="InActiveRoomPad(item.id)"
              >
                <img
                  class="roompad-action-icon roompad-action-icon--delete"
                  src="@/assets/icons/bag.svg"
                  alt=""
                  aria-hidden="true"
                />
              </button>
            </div></template
          >
          <template v-slot:no-data
            ><div class="table-empty">{{ $t("management.noRoomPadData") }}</div></template
          >
        </v-data-table>
      </div>

      <footer class="roompad-pagination">
        <v-pagination
          v-model="page"
          :length="pageCount"
          :total-visible="3"
          color="#00368e"
          @input="ChangePage(page)"
        />
        <v-select
          v-model="itemsPerPage"
          class="roompad-page-size"
          :items="items"
          dense
          solo
          flat
          hide-details
          @input="ChangePerPage(itemsPerPage)"
        />
      </footer>
    </section>

    <v-dialog v-model="RoomPadDetailDialog" persistent max-width="810px">
      <v-card class="roompad-edit-card">
        <header class="roompad-edit-header">
          <span>{{ EditStatus ? $t("management.editRoomPad") : $t("management.createRoomPad") }}</span>
          <button
            type="button"
            :aria-label="$t('management.closeRoomPadDialog')"
            @click="CloseRoomPadDetailDialog"
          >
            <v-icon color="white" size="30">mdi-close</v-icon>
          </button>
        </header>

        <div class="roompad-edit-body">
          <section class="roompad-form-section">
            <h3>{{ $t("management.generalInformation") }}</h3>
            <div
              class="roompad-form-field roompad-form-field--full"
            >
              <label for="roompad-name">{{ $t("management.name") }}</label>
              <v-text-field
                id="roompad-name"
                v-model="PadName"
                outlined
                dense
                hide-details
              />
            </div>
            <div class="roompad-form-grid">
              <div class="roompad-form-field">
                <label for="roompad-facility">{{ $t("management.facility") }}</label>
                <v-select
                  id="roompad-facility"
                  v-model="FacilityID"
                  :items="FacilityList"
                  item-text="facilityName"
                  item-value="facilityID"
                  outlined
                  dense
                  hide-details
                />
              </div>
              <div class="roompad-form-field">
                <label for="roompad-calendar">{{ $t("management.calendarType") }}</label>
                <v-select
                  id="roompad-calendar"
                  v-model="CalendarType"
                  :items="['Microsoft365', 'Google']"
                  outlined
                  dense
                  hide-details
                />
              </div>
            </div>
          </section>

          <section
            v-if="CalendarType"
            class="roompad-form-section roompad-connection-section"
          >
            <div class="roompad-section-heading">
              <h3>{{ $t("management.systemConnection") }}</h3>
              <span
                ><v-icon color="#00378e" size="21"
                  >mdi-information-outline</v-icon
                >
                {{ $t("management.calendarSyncHint") }}</span
              >
            </div>

            <template v-if="CalendarType === 'Microsoft365'">
              <div class="roompad-form-grid">
                <div class="roompad-form-field">
                  <label for="roompad-tenant">{{ $t("management.tenantId") }}</label>
                  <v-text-field
                    id="roompad-tenant"
                    v-model="TenantID"
                    outlined
                    dense
                    hide-details
                  />
                </div>
                <div class="roompad-form-field">
                  <label for="roompad-client">{{ $t("management.clientId") }}</label>
                  <v-text-field
                    id="roompad-client"
                    v-model="ClientID"
                    outlined
                    dense
                    hide-details
                  />
                </div>
              </div>
              <div class="roompad-form-field roompad-form-field--full">
                <label for="roompad-secret">{{ $t("management.clientSecret") }}</label>
                <v-text-field
                  id="roompad-secret"
                  v-model="ClientSecret"
                  :type="ShowClientSecret ? 'text' : 'password'"
                  :append-icon="
                    ShowClientSecret ? 'mdi-eye-outline' : 'mdi-eye-off-outline'
                  "
                  outlined
                  dense
                  hide-details
                  @click:append="ShowClientSecret = !ShowClientSecret"
                />
              </div>
              <div class="roompad-form-field roompad-form-field--full">
                <label for="roompad-email">{{ $t("management.userEmail") }}</label>
                <v-text-field
                  id="roompad-email"
                  v-model="UserEmail"
                  outlined
                  dense
                  hide-details
                />
              </div>
            </template>

            <div v-else class="roompad-form-field roompad-form-field--full">
              <label for="roompad-google-calendar">{{ $t("management.googleCalendarId") }}</label>
              <v-text-field
                id="roompad-google-calendar"
                v-model="GoogleCalendarID"
                outlined
                dense
                hide-details
              />
            </div>
          </section>

          <div
            class="roompad-active-choice active-status-control"
            role="radiogroup"
            :aria-label="$t('management.roomPadStatus')"
          >
            <button
              :class="{ selected: Active === '1' }"
              type="button"
              @click="Active = '1'"
            >
              {{ $t("management.active") }}
            </button>
            <button
              :class="{ selected: Active === '2' }"
              type="button"
              @click="Active = '2'"
            >
              {{ $t("management.inactive") }}
            </button>
          </div>

          <footer class="roompad-edit-actions">
            <button
              class="cancel"
              type="button"
              @click="CloseRoomPadDetailDialog"
            >
              {{ $t("management.cancel") }}
            </button>
            <button class="save" type="button" @click="SaveRoomPad">
              {{ EditStatus ? $t("management.update") : $t("management.create") }}
            </button>
          </footer>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="LoadingDialog" persistent width="auto">
      <v-card
        class="pa-6 d-flex flex-column align-center justify-center"
        elevation="8"
        rounded="xl"
      >
        <v-progress-circular
          indeterminate
          color="indigo"
          size="70"
          width="6"
        ></v-progress-circular>
        <span class="mt-4 text-subtitle-1 font-weight-medium"
          >{{ $t("management.loading") }}</span
        >
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { reportPageDataUpdated } from "@/utils/pageDataUpdated";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: "Roompad",
  components: {},
  data() {
    return {
      url: enurl.apiUrl,
      LoadingDialog: false,
      items: [10, 15, 20, 50, 100],
      pageCount: 0,
      page: 1,
      itemsPerPage: 10,
      search: "",
      no_run: 0,
      ListData: [],
      FacilityList: [],
      RoomPadDetailDialog: false,

      EditStatus: false,
      PadName: "",
      FacilityID: null,
      CalendarType: null,
      TenantID: "",
      ClientID: "",
      ClientSecret: "",
      ShowClientSecret: false,
      UserEmail: "",
      GoogleCalendarID: "",
      RoomPadID: 0,
      Active: null,
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("message.No"),
          value: "no",
          sortable: false,
          align: "start",
        },
        {
          text: this.$t("management.padName"),
          value: "padName",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("management.facility"),
          value: "facilityName",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("message.Status"),
          value: "active",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("message.Action"),
          value: "action",
          align: "center",
          sortable: false,
        },
      ];
    },
  },

  async mounted() {
    let self = this;
    await self.getDataRoomPad();
    await self.getDataDropdownFacility();
  },

  methods: {
    getDataRoomPad() {
      let self = this;
      self.LoadingDialog = true;
      let temp = {
        search: self.search,
        page: self.page,
        perPage: self.itemsPerPage,
      };
      return axios
        .post(`${self.url}Utility/getDataRoomPad`, temp)
        .then(function (response) {
          if (response.data.status == 0) {
            self.ListData = response.data.data.listData;
            self.pageCount = response.data.data.count;
            self.no_run = (self.page - 1) * self.itemsPerPage;
            reportPageDataUpdated(self);
            self.LoadingDialog = false;
          }
          self.LoadingDialog = false;
        })
        .catch(function (error) {
          self.LoadingDialog = false;
          Swal.fire({
            icon: "error",
            title: self.$t("management.error"),
            width: 500,
            text:
              error?.response?.data?.message ||
              error?.message ||
              self.$t("management.connectionError"),
          });
        });
    },

    ChangePage(value) {
      this.page = value;
      this.getDataRoomPad();
    },

    ChangePerPage(value) {
      this.itemsPerPage = value;
      this.page = 1;
      this.getDataRoomPad();
    },

    SearchDataRoomPad() {
      this.page = 1;
      this.getDataRoomPad();
    },

    getDataDropdownFacility() {
      let self = this;
      return axios
        .get(`${self.url}Utility/getDataDropdownFacility`)
        .then(function (response) {
          if (response.data.status == 0) {
            self.FacilityList = response.data.data;
          }
        })
        .catch(function (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            self.$t("management.connectionError");

          Swal.fire({
            icon: "warning",
            width: 900,
            text: message,
          });
        });
    },

    CloseRoomPadDetailDialog() {
      let self = this;
      self.EditStatus = false;
      self.RoomPadID = 0;
      self.PadName = "";
      self.FacilityID = null;
      self.CalendarType = null;
      self.TenantID = "";
      self.ClientID = "";
      self.ClientSecret = "";
      self.ShowClientSecret = false;
      self.UserEmail = "";
      self.GoogleCalendarID = "";
      self.Active = null;
      self.RoomPadDetailDialog = false;
    },

    OpenDetailRoomPadDialog(id, type) {
      let self = this;
      if (type == "Create") {
        self.EditStatus = false;
        self.RoomPadID = 0;
        self.PadName = "";
        self.FacilityID = null;
        self.CalendarType = null;
        self.TenantID = "";
        self.ClientID = "";
        self.ClientSecret = "";
        self.ShowClientSecret = false;
        self.UserEmail = "";
        self.GoogleCalendarID = "";
        self.Active = "1";
        self.RoomPadDetailDialog = true;
      } else {
        self.getDataRoomPadByID(id);
      }
    },

    getDataRoomPadByID(id) {
      let self = this;
      if (id != 0) {
        self.LoadingDialog = true;
        let temp = {
          id: id,
        };
        return axios
          .post(`${self.url}Utility/getDataRoomPadByID`, temp)
          .then(function (response) {
            if (response.data.status == 0) {
              self.BindingData(response.data.data);
            }
          })
          .catch(function (error) {
            Swal.fire({
              icon: "error",
              title: self.$t("management.error"),
              width: 500,
              text:
                error?.response?.data?.message ||
                error?.message ||
                self.$t("management.connectionError"),
            });
          })
          .finally(() => {
            self.LoadingDialog = false;
          });
      }
    },

    OpenAddRoomPadDialog() {
      this.OpenDetailRoomPadDialog(0, "Create");
    },

    BindingData(data) {
      let self = this;
      self.EditStatus = true;
      self.RoomPadID = data.id;
      self.PadName = data.padName;
      self.FacilityID = data.facilityID;
      self.CalendarType =
        data.calendarType == 1
          ? "Microsoft365"
          : data.calendarType == 2
          ? "Google"
          : null;
      self.TenantID = data.tenantID;
      self.ClientID = data.clientID;
      self.ClientSecret = data.clientSecret;
      self.UserEmail = data.userEmail;
      self.GoogleCalendarID = data.googleCalendarID;
      self.Active = data.active === true ? "1" : "2";
      self.RoomPadDetailDialog = true;
    },

    ValidateRoomPad() {
      if (!this.PadName || !this.PadName.trim()) return this.$t("management.roomPadNameRequired");
      if (!this.FacilityID) return this.$t("management.facilityRequired");
      if (!this.CalendarType) return this.$t("management.calendarTypeRequired");
      if (!this.Active) return this.$t("management.statusRequired");
      if (this.CalendarType === "Microsoft365") {
        if (!this.TenantID || !this.TenantID.trim()) return this.$t("management.tenantIdRequired");
        if (!this.ClientID || !this.ClientID.trim()) return this.$t("management.clientIdRequired");
        if (!this.ClientSecret || !this.ClientSecret.trim()) return this.$t("management.clientSecretRequired");
        if (!this.UserEmail || !this.UserEmail.trim()) return this.$t("management.userEmailRequired");
      }
      if (this.CalendarType === "Google" && (!this.GoogleCalendarID || !this.GoogleCalendarID.trim())) {
        return this.$t("management.googleCalendarIdRequired");
      }
      return null;
    },

    async SaveRoomPad() {
      let self = this;
      const validationMessage = self.ValidateRoomPad();
      if (validationMessage) {
        await Swal.fire({ icon: "warning", width: 500, text: validationMessage });
        return;
      }
      self.LoadingDialog = true;
      try {
        const payload = {
          padName: self.PadName.trim(),
          facilityID: self.FacilityID,
          calendarType: self.CalendarType === "Microsoft365" ? 1 : 2,
          tenantID: self.CalendarType === "Microsoft365" ? self.TenantID.trim() : null,
          clientID: self.CalendarType === "Microsoft365" ? self.ClientID.trim() : null,
          clientSecret: self.CalendarType === "Microsoft365" ? self.ClientSecret.trim() : null,
          userEmail: self.CalendarType === "Microsoft365" ? self.UserEmail.trim() : null,
          googleCalendarID: self.CalendarType === "Google" ? self.GoogleCalendarID.trim() : null,
          active: self.Active === "1",
        };
        const url = self.EditStatus
          ? `${self.url}Utility/editDataRoomPad`
          : `${self.url}Utility/addDataRoomPad`;
        if (self.EditStatus) {
          payload.id = self.RoomPadID;
        }
        const res = await axios.post(url, payload);
        if (res?.data?.status === 0) {
          const wasCreate = !self.EditStatus;
          Swal.fire({
            icon: "success",
            title: wasCreate ? self.$t("management.created") : self.$t("management.updated"),
            text: wasCreate ? self.$t("management.createSuccess") : self.$t("management.updateSuccess"),
            showConfirmButton: false,
            timer: 1500,
          });
          self.CloseRoomPadDetailDialog();
          if (wasCreate) self.page = 1;
          await self.getDataRoomPad();
        } else {
          throw new Error(res?.data?.message || self.$t("management.saveFailed"));
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          width: 500,
          text: error?.response?.data?.message || error.message || self.$t("management.error"),
        });
      } finally {
        self.LoadingDialog = false;
      }
    },

    async InActiveRoomPad(id) {
      let self = this;
      if (!id) return;
      const confirmation = await Swal.fire({
        icon: "warning",
        title: self.$t("management.confirmInactive"),
        text: self.$t("management.roomPadInactiveText"),
        showCancelButton: true,
        confirmButtonText: self.$t("management.confirm"),
        cancelButtonText: self.$t("management.cancel"),
      });
      if (!confirmation.isConfirmed) return;
      self.LoadingDialog = true;
      try {
        const response = await axios.post(`${self.url}Utility/deleteDataRoomPadByID`, { id });
        if (response?.data?.status !== 0) throw new Error(response?.data?.message || self.$t("management.updateFailed"));
        await Swal.fire({ icon: "success", title: self.$t("management.updated"), text: self.$t("management.updateSuccess"), showConfirmButton: false, timer: 1500 });
        await self.getDataRoomPad();
      } catch (error) {
        Swal.fire({ icon: "error", width: 500, text: error?.response?.data?.message || error.message || self.$t("management.error") });
      } finally {
        self.LoadingDialog = false;
      }
    },

    GetColor(value) {
      if (value == true) {
        return "rgb(92, 184, 92,0.3)";
      } else {
        return "rgb(232, 34, 34,0.3)";
      }
    },

    GetColorText(value) {
      if (value == true) {
        return "rgb(92, 184, 92)";
      } else {
        return "rgb(232, 34, 34)";
      }
    },

    ChangeFormatDate(date) {
      if (!date) return null;
      var DateData = new Date(date);
      var hours = DateData.getHours();
      var minutes = DateData.getMinutes();
      minutes = minutes < 10 ? "0" + minutes : minutes;
      var strTime = hours + ":" + minutes;
      return (
        DateData.getDate() +
        "/" +
        (DateData.getMonth() + 1) +
        "/" +
        DateData.getFullYear() +
        "  " +
        strTime
      );
    },
  },
};
</script>
<style scoped>
* >>> .v-data-table-header {
  background-color: black;
  color: #ffffff !important;
}
* >>> .v-data-table-header th {
  font-size: 14px !important;
  color: #ffffff !important;
}

@keyframes load {
  0% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
  40% {
    transform: scaleY(0.4);
  }
  100% {
    transform: scaleY(0.4);
  }
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.rect2 {
  animation-delay: -1.2s;
}
.rect3 {
  animation-delay: -1.1s;
}
.rect4 {
  animation-delay: -1s;
}
.rect5 {
  animation-delay: -0.9s;
}

.roompad-page {
  min-height: calc(100vh - 125px);
  padding: var(--workspace-content-top) 0 60px;
  background: #f6f6f6;
}

.roompad-edit-card {
  overflow: hidden;
  border-radius: 16px !important;
  background: #ffffff;
}

.roompad-edit-header {
  position: relative;
  display: flex;
  height: 66px;
  align-items: center;
  justify-content: center;
  background: #00378e;
  color: #ffffff;
  font-size: 21px;
  font-weight: 600;
}

.roompad-edit-header button {
  position: absolute;
  top: 15px;
  right: 22px;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  background: transparent;
}

.roompad-edit-body {
  padding: 26px 54px 40px;
}

.roompad-form-section + .roompad-form-section {
  margin-top: 48px;
}

.roompad-form-section h3 {
  margin: 0 0 18px;
  color: #595959;
  font-size: 18px;
  font-weight: 400;
}

.roompad-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.roompad-form-field--full + .roompad-form-grid,
.roompad-form-grid + .roompad-form-field--full,
.roompad-form-field--full + .roompad-form-field--full {
  margin-top: 14px;
}

.roompad-form-field label {
  display: block;
  margin: 0 0 7px 1px;
  color: #353535;
  font-size: 13px;
  font-weight: 500;
}

.roompad-form-field ::v-deep .v-input__slot {
  min-height: 50px !important;
  border-radius: 26px !important;
  background: #ffffff !important;
}

.roompad-form-field ::v-deep fieldset {
  border-color: #dddddd !important;
}

.roompad-form-field ::v-deep .v-input--is-focused fieldset {
  border-color: #00378e !important;
}

.roompad-form-field ::v-deep input,
.roompad-form-field ::v-deep .v-select__selection {
  color: #282828;
  font-size: 14px;
}

.roompad-form-field ::v-deep .v-input__append-inner .v-icon {
  color: #00378e !important;
}

.roompad-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.roompad-section-heading span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #777777;
  font-size: 13px;
}

.roompad-active-choice {
  display: flex;
  gap: 22px;
  margin-top: 28px;
}

.roompad-active-choice button {
  width: 145px;
  height: 50px;
  border: 0;
  border-radius: 26px;
  background: #ffffff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.24);
  color: #282828;
  font-size: 14px;
}

.roompad-active-choice button.selected {
  border: 3px solid #00378e;
  box-shadow: none;
}

.roompad-edit-actions {
  display: flex;
  justify-content: center;
  gap: 48px;
  margin-top: 88px;
}

.roompad-edit-actions button {
  width: 174px;
  height: 60px;
  border: 0;
  border-radius: 30px;
  font-size: 16px;
}

.roompad-edit-actions .cancel {
  background: #eeeeee;
  color: #00378e;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
}

.roompad-edit-actions .save {
  background: #00378e;
  color: #ffffff;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.22);
}

.roompad-toolbar {
  display: grid;
  grid-template-columns: minmax(420px, 660px) 157px;
  align-items: center;
  justify-content: center;
  gap: 23px;
  margin-bottom: 64px;
}

.roompad-search {
  display: flex;
  height: 60px;
  align-items: center;
  padding: 7px 9px 8px 36px;
  border-radius: 60px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.roompad-search input {
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  color: #191c1e;
  font-size: 18px;
}

.roompad-search input::placeholder {
  color: #595959;
  opacity: 1;
}

.roompad-search button {
  display: grid;
  width: 45px;
  height: 45px;
  flex: 0 0 45px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #00378e;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.22);
}

.roompad-search button img {
  display: block;
  width: 23px;
  height: 23px;
}

.create-roompad {
  height: 60px;
  border: 0;
  border-radius: 60px;
  background: #00378e;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  color: #ffffff;
  font-size: 20px;
  font-weight: 500;
}

.roompad-panel {
  position: relative;
  box-sizing: border-box;
  min-height: 436px;
  padding: 31px 18px 110px;
  border-radius: 30px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.roompad-table-wrap {
  overflow-x: auto;
  border-radius: 30px 30px 0 0;
}

.roompad-table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  color: #4c4c4c;
  font-size: 15px;
}
.roompad-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}
.roompad-table ::v-deep table {
  min-width: 900px;
  table-layout: fixed;
}
.roompad-table ::v-deep .v-data-table-header {
  background: #00378e;
}
.roompad-table ::v-deep .v-data-table-header th {
  height: 81px;
  padding: 0 24px !important;
  border-bottom: 0 !important;
  color: #fff !important;
  font-size: 18px;
  font-weight: 400;
  white-space: nowrap;
}
.roompad-table ::v-deep tbody td {
  height: 66px;
  padding: 0 24px !important;
  border-bottom: 0 !important;
  color: #4c4c4c;
  font-size: 15px;
}
.roompad-table ::v-deep th:first-child,
.roompad-table ::v-deep td:first-child {
  width: 12%;
}
.roompad-table ::v-deep th:nth-child(2),
.roompad-table ::v-deep td:nth-child(2),
.roompad-table ::v-deep th:nth-child(3),
.roompad-table ::v-deep td:nth-child(3) {
  width: 28%;
}
.roompad-table ::v-deep th:nth-child(4),
.roompad-table ::v-deep td:nth-child(4),
.roompad-table ::v-deep th:last-child,
.roompad-table ::v-deep td:last-child {
  width: 16%;
  text-align: center;
}
.roompad-table ::v-deep tbody tr {
  background: #fff;
}
.roompad-table ::v-deep tbody tr:nth-child(even) {
  background: #f6f6f6;
}
.table-empty {
  padding: 32px 16px;
  color: #595959;
  text-align: center;
}

.roompad-table thead {
  height: 81px;
  background: #00378e;
  color: #ffffff;
  font-size: 18px;
}

.roompad-table th,
.roompad-table td {
  padding: 0 38px;
  text-align: left;
}

.roompad-table th {
  font-weight: 400;
  white-space: nowrap;
}

.roompad-table th:first-child,
.roompad-table td:first-child {
  width: 150px;
}

.roompad-table th:nth-child(2),
.roompad-table td:nth-child(2) {
  width: 310px;
}

.roompad-table th:nth-child(4),
.roompad-table td:nth-child(4) {
  width: 230px;
  text-align: center;
}

.roompad-table th:last-child,
.roompad-table td:last-child {
  width: 210px;
  text-align: center;
}

.roompad-table tbody tr {
  height: 66px;
  background: #ffffff;
}

.roompad-table tbody tr:nth-child(even) {
  background: #f6f6f6;
}

.roompad-status {
  display: inline-flex;
  min-width: 94px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: #91d589;
  color: #3f6840;
  font-size: 15px;
}

.roompad-status.inactive {
  background: #f1aaaa;
  color: #812424;
}

.roompad-actions {
  display: flex;
  height: 66px;
  align-items: center;
  justify-content: center;
  gap: 26px;
}

.roompad-actions button {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #ff8500;
}

.roompad-actions button.delete {
  background: #d00000;
}

.roompad-action-icon {
  display: block;
  object-fit: contain;
}

.roompad-action-icon--edit {
  width: 18px;
  height: 18px;
}

.roompad-action-icon--delete {
  width: 24px;
  height: 24px;
}

.roompad-pagination {
  position: absolute;
  right: 18px;
  bottom: 33px;
  left: 18px;
  display: flex;
  height: 59px;
  align-items: center;
  justify-content: center;
}

.roompad-page-size {
  position: absolute;
  right: 50px;
  width: 133px;
  max-width: 133px;
}

.roompad-page-size ::v-deep .v-input__slot {
  min-height: 50px;
  border-radius: 30px !important;
  background: #f6f6f6 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25) !important;
}

.roompad-pagination ::v-deep .v-pagination__item,
.roompad-pagination ::v-deep .v-pagination__navigation {
  width: 49px;
  min-width: 49px;
  height: 49px;
  border-radius: 10px;
}

@media (max-width: 1200px) {
  .roompad-page {
    padding-right: 24px;
    padding-left: 24px;
  }

  .roompad-toolbar {
    grid-template-columns: minmax(320px, 1fr) 157px;
  }
}

@media (max-width: 760px) {
  .roompad-page {
    padding: 24px 12px;
  }

  .roompad-toolbar {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 28px;
  }

  .create-roompad {
    width: 157px;
    justify-self: end;
  }

  .roompad-panel {
    padding: 12px 12px 150px;
    border-radius: 16px;
  }

  .roompad-pagination {
    bottom: 24px;
  }

  .roompad-page-size {
    right: 50%;
    bottom: -66px;
    transform: translateX(50%);
  }

  .roompad-edit-body {
    padding: 24px 18px 28px;
  }

  .roompad-form-section + .roompad-form-section {
    margin-top: 32px;
  }

  .roompad-form-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .roompad-section-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .roompad-active-choice {
    gap: 12px;
  }

  .roompad-active-choice button {
    width: 50%;
  }

  .roompad-edit-actions {
    gap: 12px;
    margin-top: 48px;
  }

  .roompad-edit-actions button {
    width: 50%;
  }
}

/* Compact desktop density; dialog and mobile sizing remain unchanged. */
@media (min-width: 761px) {
  .roompad-edit-header {
    height: 59px;
    font-size: 19px;
  }

  .roompad-edit-header button {
    top: 13px;
    right: 20px;
    width: 32px;
    height: 32px;
  }

  .roompad-edit-body {
    padding: 19px 49px 28px;
  }

  .roompad-form-section + .roompad-form-section {
    margin-top: 34px;
  }

  .roompad-form-section h3 {
    margin-bottom: 12px;
    font-size: 16px;
  }

  .roompad-form-grid {
    gap: 22px;
  }

  .roompad-form-field--full + .roompad-form-grid,
  .roompad-form-grid + .roompad-form-field--full,
  .roompad-form-field--full + .roompad-form-field--full {
    margin-top: 10px;
  }

  .roompad-form-field ::v-deep .v-input__slot {
    min-height: 45px !important;
    border-radius: 23px !important;
  }

  .roompad-form-field ::v-deep input,
  .roompad-form-field ::v-deep .v-select__selection {
    font-size: 13px;
  }

  .roompad-active-choice {
    gap: 20px;
    margin-top: 20px;
  }

  .roompad-active-choice button {
    width: 131px;
    height: 45px;
    border-radius: 23px;
    font-size: 13px;
  }

  .roompad-edit-actions {
    gap: 43px;
    margin-top: 54px;
  }

  .roompad-edit-actions button {
    width: 157px;
    height: 54px;
    border-radius: 27px;
    font-size: 14px;
  }

  .roompad-page {
    min-height: calc(100vh - 104px);
    padding: 36px 0;
  }

  .roompad-toolbar {
    grid-template-columns: minmax(360px, 560px) 126px;
    gap: 16px;
    margin-bottom: 28px;
  }

  .roompad-search,
  .create-roompad {
    height: 46px;
  }

  .roompad-search {
    padding: 5px 6px 5px 22px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  }

  .roompad-search input {
    font-size: 15px;
  }

  .roompad-search button {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    box-shadow: none;
  }

  .roompad-search button img {
    width: 18px;
    height: 18px;
  }

  .create-roompad {
    font-size: 16px;
  }

  .roompad-panel {
    min-height: 360px;
    padding: 20px 14px 90px;
    border-radius: 16px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  .roompad-table-wrap {
    border-radius: 12px 12px 0 0;
  }

  .roompad-table ::v-deep .v-data-table-header th {
    height: 56px;
    padding: 0 20px !important;
    font-size: 15px;
  }

  .roompad-table ::v-deep tbody td {
    height: 50px;
    padding: 0 20px !important;
    font-size: 13px;
  }

  .roompad-status {
    min-width: 78px;
    height: 26px;
    font-size: 13px;
  }

  .roompad-actions {
    height: 50px;
    gap: 16px;
  }

  .roompad-actions button {
    width: 34px;
    height: 34px;
  }

  .roompad-pagination {
    right: 14px;
    bottom: 24px;
    left: 14px;
    height: 44px;
  }

  .roompad-page-size {
    right: 30px;
    width: 112px;
    max-width: 112px;
  }

  .roompad-page-size ::v-deep .v-input__slot {
    min-height: 42px;
  }

  .roompad-pagination ::v-deep .v-pagination__item,
  .roompad-pagination ::v-deep .v-pagination__navigation {
    width: 40px;
    min-width: 40px;
    height: 40px;
    border-radius: 8px;
  }
}
</style>
